/**
 * `Context_Executor.ts` — port of Unity's `Executor/Context_Executor.cs`
 * for the web-registered context tools. Context digests come from the
 * context engine; the screenshot path captures the Three.js canvas and
 * hands it to the session as an `input_image` (Unity `SendUserImage`).
 */

import { Ioc } from "@core/ioc/ioc";
import { ENGINE_SERVICE, type EngineService } from "@runtime/engine/engineService";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import type { IAIContextEngineModel } from "@ai/context/AIContextEngineModel.Core";
import { withDocumentDisplayNames } from "./citationDisplayNames";
import {
  getString,
  getStringList,
  type AgentToolExecutorHost,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

const SCREENSHOT_MAX_WIDTH = 1280;

export class ContextExecutor implements IDomainExecutor {
  private readonly m_contextEngine: IAIContextEngineModel;
  private readonly m_host: AgentToolExecutorHost;

  constructor(contextEngine: IAIContextEngineModel, host: AgentToolExecutorHost) {
    this.m_contextEngine = contextEngine;
    this.m_host = host;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.GetProjectContext:
        return this.m_contextEngine.buildProjectContext();
      case AgentToolNames.GetSceneContext: {
        const prompt = getString(args, "prompt");
        const searchQuery = getStringList(args, "SearchQuery");
        return this.m_contextEngine.querySceneContext(prompt, searchQuery);
      }
      case AgentToolNames.GetPartContext: {
        const partPath = getString(args, "partPath");
        if (!partPath) return "error: Get_Part_Context_missing_partPath";
        const prompt = getString(args, "prompt");
        // Unity `Context_Executor.cs:141` publishes the composed docs prompt +
        // any citations to the conversation panel, so the user can see WHAT was
        // searched and open the sources.
        const presentation = this.m_host.conversationPresentation;
        const result = await this.m_contextEngine.queryPartContextAsync(
          prompt ?? "",
          partPath,
        );
        if (result.docsPrompt) {
          presentation?.publishToolUsagePrompt(AgentToolNames.GetPartContext, result.docsPrompt);
        }
        // Ranked sources, not the cited subset — same reason as
        // `Prompt_Search_Docs`; this shares the same transport, INCLUDING its
        // `searchRan` rule. `sources` is null both when no search ran and when
        // one ran and matched only noise, and `citations` is the raw unfiltered
        // hit list - so the bare `??` published the documents the relevance
        // floor had just rejected. This path is deliberately exempt from the
        // not-found backstop (part notes are a legitimate answer here), which
        // makes it the one place that combination is still reachable.
        const sources = await withDocumentDisplayNames(
          this.m_host,
          result.response?.searchRan
            ? result.response.sources ?? null
            : result.response?.sources ?? result.response?.citations ?? null,
        );
        if (sources?.length) {
          presentation?.publishCitations(sources);
        }
        this.m_host.trackHelperAgentUsage?.(
          "Knowledge Context Agent",
          result.response?.usage ?? null,
        );
        return result.text;
      }
      case AgentToolNames.TakeScreenshot:
        return this.takeScreenshot();
      default:
        return null;
    }
  }

  private async takeScreenshot(): Promise<string> {
    if (!Ioc.isRegistered<EngineService>(ENGINE_SERVICE)) {
      return "tool_error: take_screenshot_viewport_unavailable";
    }
    let canvas: HTMLCanvasElement;
    try {
      canvas = Ioc.resolve<EngineService>(ENGINE_SERVICE).app.canvas;
    } catch {
      return "tool_error: take_screenshot_viewport_unavailable";
    }

    const dataUrl = downscaleToJpeg(canvas, SCREENSHOT_MAX_WIDTH);
    if (!dataUrl) return "error: take_screenshot_capture_failed";

    const sent = this.m_host.sendUserImage(dataUrl);
    return sent
      ? "ok: screenshot of the user's current view attached to the conversation"
      : "error: take_screenshot_no_active_session";
  }
}

function downscaleToJpeg(source: HTMLCanvasElement, maxWidth: number): string | null {
  try {
    const scale = Math.min(1, maxWidth / Math.max(1, source.width));
    if (scale >= 1) return source.toDataURL("image/jpeg", 0.85);
    const target = document.createElement("canvas");
    target.width = Math.round(source.width * scale);
    target.height = Math.round(source.height * scale);
    const ctx = target.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(source, 0, 0, target.width, target.height);
    return target.toDataURL("image/jpeg", 0.85);
  } catch {
    return null;
  }
}
